import { ChatWidget } from './chat';
import { createRoot, Root } from 'react-dom/client';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { parseEmbedStyle, type LauncherEmbedStyle } from '@/lib/embed-style';
import { resolveDockModeEnabled } from '@/lib/embed-dock-mode';
import {
  installCapioEngageOnWindow,
  seedMetadataFromScriptAttributes,
} from './sdk';

class CapioEngageWidgetWebComponent extends HTMLElement {
  private _root?: Root;
  private _engageId: string = '';
  private _serviceUnavailableMessage: string = '';
  private _launcherEmbedStyle: LauncherEmbedStyle = parseEmbedStyle(null);
  private _dockModeEnabled: boolean = false;

  connectedCallback() {
    if (!this._root) {
      // Updated 2026-02-02: Use Shadow DOM to prevent host website CSS from
      // leaking into the widget. Also inject Emotion/MUI styles into the shadow root.
      const shadowRoot = this.attachShadow({ mode: 'open' });

      const resetStyles = document.createElement('style');
      resetStyles.textContent = `
        :host { all: initial; }
        *, *::before, *::after { box-sizing: border-box; }
      `;
      shadowRoot.appendChild(resetStyles);

      const mount = document.createElement('div');
      shadowRoot.appendChild(mount);

      const cache = createCache({
        key: 'capio-engage-widget',
        // Emotion supports inserting styles into a ShadowRoot container.
        // Updated 2026-02-02: Cast to satisfy narrowed TypeScript types.
        container: shadowRoot as unknown as HTMLElement,
        prepend: true,
      });

      this._root = createRoot(mount);
      this._root.render(
        <CacheProvider value={cache}>
          <ChatWidget
            engageId={this._engageId}
            serviceUnavailableMessage={this._serviceUnavailableMessage || undefined}
            launcherEmbedStyle={this._launcherEmbedStyle}
            dockModeEnabled={this._dockModeEnabled}
          />
        </CacheProvider>
      );
    }
  }

  disconnectedCallback() {
    this._root?.unmount();
  }

  set engageId(value: string) {
    this._engageId = value;
  }

  get engageId(): string {
    return this._engageId;
  }

  set serviceUnavailableMessage(value: string) {
    this._serviceUnavailableMessage = value;
  }

  get serviceUnavailableMessage(): string {
    return this._serviceUnavailableMessage;
  }

  set launcherEmbedStyle(value: LauncherEmbedStyle) {
    this._launcherEmbedStyle = value;
  }

  get launcherEmbedStyle(): LauncherEmbedStyle {
    return this._launcherEmbedStyle;
  }

  set dockModeEnabled(value: boolean) {
    this._dockModeEnabled = value;
  }

  get dockModeEnabled(): boolean {
    return this._dockModeEnabled;
  }
}

if (
  typeof window !== 'undefined' &&
  !customElements.get('capio-engage-widget')
) {
  customElements.define('capio-engage-widget', CapioEngageWidgetWebComponent);

  // Get engage ID and service unavailable message from script tag or environment variable
  const currentScript =
    (document.currentScript as HTMLScriptElement | null) ??
    [...document.querySelectorAll<HTMLScriptElement>('script[src*="capio-engage-widget.js"]')].at(-1) ??
    null;
  const engageId =
    currentScript?.dataset?.engageId ||
    import.meta.env.VITE_WIDGET_ENGAGE_ID ||
    '';
  const serviceUnavailableMessage = currentScript?.dataset?.serviceUnavailableMessage || '';
  const launcherEmbedStyle = parseEmbedStyle(currentScript?.getAttribute('style'));
  const dockModeEnabled = resolveDockModeEnabled(currentScript);

  if (currentScript) {
    seedMetadataFromScriptAttributes(currentScript);
  }
  installCapioEngageOnWindow();

  const widget = document.createElement(
    'capio-engage-widget'
  ) as CapioEngageWidgetWebComponent;
  widget.engageId = engageId;
  widget.serviceUnavailableMessage = serviceUnavailableMessage;
  widget.launcherEmbedStyle = launcherEmbedStyle;
  widget.dockModeEnabled = dockModeEnabled;

  // Insert the widget next to the script tag when possible.
  // This makes local/integration testing predictable when the script is placed
  // in a specific part of the DOM.
  if (currentScript?.parentNode) {
    currentScript.parentNode.insertBefore(widget, currentScript.nextSibling);
  } else {
    document.body.appendChild(widget);
  }
}
